import React, {PropsWithChildren} from 'react';
import {
    ListItem,
    Typography,
    TextLabel,
    BulletIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    Tooltip,
    TooltipHandler
} from '@genestack/ui';
import classNames from 'classnames';
import {useFoldingItemsState} from '../../../../../hooks/use-folding-items-state';
import styles from './folding-item.module.css';
import rightPanelStyles from './right-panel.module.css';
import {HighlightedText} from '../../../../../components/highlighted-text';
import {ViewDeclarationButton} from './statistics/view-declaration-button';

export type FoldingItemProps = PropsWithChildren<{
    label: string;
    foldingStateProps: ReturnType<typeof useFoldingItemsState>;
    foldingStateKey: string;
    count?: number;
    padding: 1 | 2;
    searchString?: string;
    isUnfoldedByDefault?: boolean;
    declarationUrl?: string;
}>;

type AlwaysUnfoldedItemProps = Omit<FoldingItemProps, 'foldingStateProps' | 'foldingStateKey'> & {
    isAlwaysUnfolded: true;
};

function isAlwaysUnfoldedItemProps(
    props: FoldingItemProps | AlwaysUnfoldedItemProps
): props is AlwaysUnfoldedItemProps {
    return (props as AlwaysUnfoldedItemProps).isAlwaysUnfolded;
}

export function FoldingItem(props: FoldingItemProps | AlwaysUnfoldedItemProps) {
    const isAlwaysUnfoldedItem = isAlwaysUnfoldedItemProps(props);
    const isUnfolded = (function () {
        if (isAlwaysUnfoldedItem) {
            return true;
        }

        if (props.isUnfoldedByDefault) {
            return props.foldingStateProps.foldingState[props.foldingStateKey] !== false;
        }

        return props.foldingStateProps.foldingState[props.foldingStateKey];
    })();

    const onClick = React.useCallback(() => {
        if (isAlwaysUnfoldedItem) {
            return;
        }

        if (isUnfolded) {
            props.foldingStateProps.fold(props.foldingStateKey);
        } else {
            props.foldingStateProps.unfold(props.foldingStateKey);
        }
    }, [props, isAlwaysUnfoldedItem, isUnfolded]);

    const prependIcon = (function () {
        if (isAlwaysUnfoldedItem) {
            return null;
        }

        if (isUnfolded) {
            return <ChevronDownIcon />;
        }

        return <ChevronRightIcon />;
    })();

    return (
        <React.Fragment>
            <ListItem
                interactive={!isAlwaysUnfoldedItem}
                prepend={prependIcon}
                onClick={onClick}
                className={classNames(
                    rightPanelStyles.statItem,
                    props.padding === 2 && styles.statItemLevel2
                )}
            >
                <TextLabel
                    caption={
                        <div className={rightPanelStyles.listItemCaption}>
                            {props.count !== undefined ? (
                                <Typography intent="quiet">
                                    <BulletIcon />
                                    {props.count}
                                </Typography>
                            ) : undefined}

                            {props.declarationUrl && (
                                <ViewDeclarationButton declarationUrl={props.declarationUrl} />
                            )}
                        </div>
                    }
                >
                    <TooltipHandler
                        tooltip={<Tooltip className={styles.labelTooltip}>{props.label}</Tooltip>}
                    >
                        <div>
                            <HighlightedText text={props.label} value={props.searchString || ''} />
                        </div>
                    </TooltipHandler>
                </TextLabel>
            </ListItem>

            {isUnfolded && props.children}
        </React.Fragment>
    );
}
